/**
 * In-memory result set for assistant search follow-ups.
 * The first search seeds this list from the catalog; every later filter
 * treats these rows as the only database (never the full catalog dump).
 */

let session = false;
let seeded = false;
let hits: Record<string, unknown>[] = [];

type AssistantSearchHitsListener = (rows: Record<string, unknown>[]) => void;
const hitsListeners = new Set<AssistantSearchHitsListener>();

function notifyAssistantSearchHitsLoaded(rows: Record<string, unknown>[]): void {
  hitsListeners.forEach((listener) => {
    listener(rows);
  });
}

/** Subscribe to loaded hit-list commits (including empty). */
export function subscribeAssistantSearchHits(
  listener: AssistantSearchHitsListener,
): () => void {
  hitsListeners.add(listener);
  return () => {
    hitsListeners.delete(listener);
  };
}

export function beginAssistantSearchHitsSession(): void {
  session = true;
  seeded = false;
  hits = [];
}

export function clearAssistantSearchHits(): void {
  session = false;
  seeded = false;
  hits = [];
}

export function isAssistantSearchHitsSession(): boolean {
  return session;
}

/** True after the first search has committed its displayed rows. */
export function isAssistantSearchHitsReady(): boolean {
  return session && seeded;
}

export function getAssistantSearchHits(): Record<string, unknown>[] {
  return hits;
}

/**
 * Store the listings currently on screen.
 * Pass `loaded: true` after the fetch finishes so an empty first search can
 * seed; follow-ups that match nothing also keep `[]`.
 * Empty commits without `loaded` are ignored until seeded, so a spinner
 * cannot wipe the set.
 */
export function commitAssistantSearchHits(
  rows: Record<string, unknown>[],
  options?: { loaded?: boolean },
): void {
  if (!session) {
    return;
  }
  if (rows.length === 0 && !seeded && !options?.loaded) {
    return;
  }
  hits = rows;
  seeded = true;
  if (options?.loaded) {
    notifyAssistantSearchHitsLoaded(hits);
  }
}

/** True when the current assistant search finished and showed no listings. */
export function isAssistantSearchHitsEmpty(): boolean {
  return session && seeded && hits.length === 0;
}
